import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const SLIDE_INTERVAL_MS = 4000;

const bgSlide1Def: SlotGroupDefinition = {
  label: 'Background Image 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const bgSlide2Def: SlotGroupDefinition = {
  label: 'Background Image 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const bgSlide3Def: SlotGroupDefinition = {
  label: 'Background Image 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const bgSlide4Def: SlotGroupDefinition = {
  label: 'Background Image 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const bgSlide5Def: SlotGroupDefinition = {
  label: 'Background Image 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const bgSlide6Def: SlotGroupDefinition = {
  label: 'Background Image 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const bg1 = useSlotGroup('hero.bg_image_1', bgSlide1Def);
  const bg2 = useSlotGroup('hero.bg_image_2', bgSlide2Def);
  const bg3 = useSlotGroup('hero.bg_image_3', bgSlide3Def);
  const bg4 = useSlotGroup('hero.bg_image_4', bgSlide4Def);
  const bg5 = useSlotGroup('hero.bg_image_5', bgSlide5Def);
  const bg6 = useSlotGroup('hero.bg_image_6', bgSlide6Def);

  const slides = [
    { path: 'hero.bg_image_1', src: bg1.image || '' },
    { path: 'hero.bg_image_2', src: bg2.image || '' },
    { path: 'hero.bg_image_3', src: bg3.image || '' },
    { path: 'hero.bg_image_4', src: bg4.image || '' },
    { path: 'hero.bg_image_5', src: bg5.image || '' },
    { path: 'hero.bg_image_6', src: bg6.image || '' },
  ];
  const filled = slides.filter((slide) => !!slide.src);

  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const count = filled.length;
  const safeCurrent = count > 0 ? current % count : 0;
  const activePath = count > 0 ? filled[safeCurrent]?.path : null;

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (count <= 1) return;
      setCurrent((prev) => (prev + dir + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReducedMotion(true);
    }
  }, []);

  useEffect(() => {
    if (count <= 1 || paused || reducedMotion) return;
    const timerId = setInterval(() => advance(1), SLIDE_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, [count, paused, reducedMotion, advance]);

  // Keep index in range when filled slides shrink.
  useEffect(() => {
    if (count === 0) {
      setCurrent(0);
      return;
    }
    setCurrent((prev) => prev % count);
  }, [count]);

  // Parallax refs
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const slideImage = (src: string, path: string, priority: boolean) => {
    const isActive = path === activePath;
    if (!src) {
      // Keep an edit-mode hit target without display:none
      return <div className="w-full h-full opacity-0" aria-hidden />;
    }
    return (
      <VibesitesImage
        url={src}
        alt=""
        priority={priority}
        sizes="100vw"
        className={`hero-bg-slide${isActive ? ' hero-bg-slide--active' : ''}`}
        style={
          isActive && !reducedMotion
            ? {
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as CSSProperties['imageRendering'],
              }
            : undefined
        }
      />
    );
  };

  return (
    <section ref={sectionRef} className="relative w-full h-screen overflow-hidden">
      {/* Parallax background slider */}
      <div
        ref={imgWrapRef}
        className="absolute z-0 h-[140%]"
        style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
      >
        <div
          className="hero-bg-slider relative w-full h-full"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {count === 0 && (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
          <div className="absolute inset-0">
            <EditableSlotGroup
              groupPath="hero.bg_image_1"
              definition={bgSlide1Def}
              className="block w-full h-full"
            >
              {slideImage(bg1.image || '', 'hero.bg_image_1', true)}
            </EditableSlotGroup>
          </div>
          <div className="absolute inset-0">
            <EditableSlotGroup
              groupPath="hero.bg_image_2"
              definition={bgSlide2Def}
              className="block w-full h-full"
            >
              {slideImage(bg2.image || '', 'hero.bg_image_2', false)}
            </EditableSlotGroup>
          </div>
          <div className="absolute inset-0">
            <EditableSlotGroup
              groupPath="hero.bg_image_3"
              definition={bgSlide3Def}
              className="block w-full h-full"
            >
              {slideImage(bg3.image || '', 'hero.bg_image_3', false)}
            </EditableSlotGroup>
          </div>
          <div className="absolute inset-0">
            <EditableSlotGroup
              groupPath="hero.bg_image_4"
              definition={bgSlide4Def}
              className="block w-full h-full"
            >
              {slideImage(bg4.image || '', 'hero.bg_image_4', false)}
            </EditableSlotGroup>
          </div>
          <div className="absolute inset-0">
            <EditableSlotGroup
              groupPath="hero.bg_image_5"
              definition={bgSlide5Def}
              className="block w-full h-full"
            >
              {slideImage(bg5.image || '', 'hero.bg_image_5', false)}
            </EditableSlotGroup>
          </div>
          <div className="absolute inset-0">
            <EditableSlotGroup
              groupPath="hero.bg_image_6"
              definition={bgSlide6Def}
              className="block w-full h-full"
            >
              {slideImage(bg6.image || '', 'hero.bg_image_6', false)}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      {/* Soft full-bleed gradient — decorative; must not intercept clicks */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-t from-black/22 via-black/8 to-black/4"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 55% at 50% 50%, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.10) 45%, transparent 72%)',
        }}
        aria-hidden="true"
      />

      <div
        className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8 pointer-events-none box-border"
        style={{ paddingTop: 'var(--header-height, 5rem)' }}
      >
        <RichTextContent
          path="hero.pre_title"
          label="Pre-Title"
          defaultValue="<p>Welcome to</p>"
          as="p"
          className="hero-entrance hero-entrance-1 hero-pre-title-shadow font-body text-sm sm:text-base md:text-lg tracking-[0.25em] uppercase text-white mb-2 pointer-events-auto"
        />
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title-shadow font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-5 max-w-5xl leading-[1.1] tracking-tight text-white uppercase pointer-events-auto"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-4 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300 pointer-events-auto"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-entrance-4 { animation-delay: 0.8s; }

        .hero-bg-slide {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }
        .hero-bg-slide--active {
          opacity: 1;
        }

        .hero-cta {
          background: rgba(0, 0, 0, 0.18);
          border: 1.5px solid rgba(255, 255, 255, 0.75);
          border-radius: 2px;
          color: #fff;
          box-shadow: none;
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: #fff;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .hero-cta:hover {
          background: rgba(0, 0, 0, 0.18);
          border-color: #fff;
          color: var(--vibe-color-text);
          box-shadow: none;
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          opacity: 1;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .hero-pre-title-shadow {
          text-shadow:
            0 1px 3px rgba(0, 0, 0, 0.28),
            0 2px 14px rgba(0, 0, 0, 0.18);
        }
        .hero-title-shadow {
          text-shadow:
            0 1px 3px rgba(0, 0, 0, 0.32),
            0 3px 18px rgba(0, 0, 0, 0.22);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-bg-slide { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.pre_title': {
      label: 'Pre-Title',
      type: 'richtext',
      defaultValue: '<p>Welcome to</p>',
    },
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image_1': bgSlide1Def,
    'hero.bg_image_2': bgSlide2Def,
    'hero.bg_image_3': bgSlide3Def,
    'hero.bg_image_4': bgSlide4Def,
    'hero.bg_image_5': bgSlide5Def,
    'hero.bg_image_6': bgSlide6Def,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
